import React, { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { faFile } from '@fortawesome/pro-light-svg-icons/faFile';
import { faPaperclip } from '@fortawesome/pro-light-svg-icons/faPaperclip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { partial } from 'lodash';
import PropTypes from 'prop-types';
import { colors } from 'theme/constants';

import { sendTeamEmail } from 'actions/teamEmails';

import { buildLocationOptions } from 'features/teamView/util';

import Box from 'components/Box/index';
import Button from 'components/clickables/Button';
import IconButton from 'components/clickables/IconButton';
import CheckboxField from 'components/forms/CheckboxField';
import Dropzone from 'components/forms/Dropzone';
import SelectInput from 'components/forms/SelectInput/SelectInput';
import TextInput from 'components/forms/TextInput/TextInput';
import withUxTracking from 'components/hocs/withUxTracking';
import Icon from 'components/Icon';
import Text, { Paragraph } from 'components/Text/index';
import TooltipIcon from 'components/TooltipIcon';

import cxHelpers from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

const FILE_TYPES = ['.pdf', '.jpeg', '.jpg', '.png', '.docx', '.mp3', '.mp4'];

const element = 'announcement_send_button';

@connect(() => ({}), { sendTeamEmail })
@withUxTracking(
  PRODUCT_AREAS.TEAM_ANNOUNCEMENTS,
  EVENT_CATEGORIES.ANNOUNCEMENTS
)
@cxHelpers('NewEmail')
export default class NewEmail extends PureComponent {
  static propTypes = {
    onReturn: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    locations: ImmutablePropTypes.map.isRequired,
    teamEmailAudiences: ImmutablePropTypes.list.isRequired,
    showAnnouncementsV2: PropTypes.bool,
    sendTeamEmail: PropTypes.func.isRequired,
  };

  state = {
    locationIds: [this.props.locations.first().get('id')],
    audience: this.props.teamEmailAudiences.first(),
    subject: '',
    message: '',
    pinned: !this.props.showAnnouncementsV2,
    requireAcknowledgement: false,
    view: 'form',
    files: [],
    newFiles: [],
    isSubmitting: false,
  };

  locationOptions = buildLocationOptions(this.props.locations);

  audienceOptions = this.props.teamEmailAudiences.map(value => ({
    value,
    label: toI18n(`message_drawer.team_emails.audiences.${value}`),
  }));

  handleReturn = () => {
    this.props.onReturn();
  };

  handleSend = () => {
    const {
      locationIds,
      audience,
      subject,
      message,
      pinned,
      files,
      requireAcknowledgement,
    } = this.state;

    if (this.props.trackUxEvent) {
      this.props.trackUxEvent({
        eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
        properties: {
          element,
          button_text: toI18n('message_drawer.team_emails.send_message'),
          pin: pinned,
          require_ack: requireAcknowledgement,
        },
      });
    }

    this.setState({ isSubmitting: true });

    this.props.sendTeamEmail({
      locationIds,
      audience,
      subject,
      message,
      pinned,
      files,
      requireAcknowledgement,
    });
  };

  handleInputChange(fieldName) {
    return value => this.setState({ [fieldName]: value });
  }

  handleLocationChange = this.handleInputChange('locationIds');

  handleSubjectChange = this.handleInputChange('subject');

  handleMessageChange = this.handleInputChange('message');

  handleAudienceChange = this.handleInputChange('audience');

  renderHeader() {
    return (
      <Box pvxs bb row spacebetween vcenter>
        <Box plxxs row vcenter>
          <IconButton
            onClick={this.handleReturn}
            type="chevronLeft"
            iconColor="blue"
            size="sm"
            cramped
          />
          <Box plxxs w={270} ellipsis>
            <Text
              black
              fs18
              fw600
              i18n="message_drawer.team_emails.new_announcement"
            />
          </Box>
        </Box>
        <Box mla>
          <IconButton
            onClick={this.props.onClose}
            type="mdClose"
            iconColor="grayDark"
            size="sm"
          />
        </Box>
      </Box>
    );
  }

  renderLabel(i18n) {
    return <Paragraph mb4 mt16 black fs14 fw600 i18n={i18n} />;
  }

  renderLocationsSelect() {
    return (
      <Box>
        {this.renderLabel('message_drawer.team_emails.location_label')}
        <SelectInput
          onChange={this.handleLocationChange}
          multi
          options={this.locationOptions}
          value={this.state.locationIds}
          clearable={false}
        />
      </Box>
    );
  }

  handleShowAttachmentScreen = () => {
    this.setState({ newFiles: this.state.files });
    this.setState({ view: 'attachments' });
  };

  handleCancel = () => this.setState({ view: 'form' });

  handleUpload = () => {
    this.setState({ files: this.state.newFiles });
    this.handleCancel();
  };

  handleTogglePinned = () =>
    this.setState({
      pinned: !this.state.pinned,
    });

  handleToggleRequireAcknowledgement = () =>
    this.setState({
      requireAcknowledgement: !this.state.requireAcknowledgement,
    });

  handleDropFile = e => {
    if (e) {
      this.setState({ newFiles: [...this.state.newFiles, e] });
    }
  };

  handleRemoveFile = file =>
    this.setState({ newFiles: this.state.newFiles.filter(f => f !== file) });

  renderLabelWithTooltip = i18nKey => (
    <Box row>
      <Box>
        <Text
          fs14
          navy
          lh={1.29}
          i18n={`message_drawer.team_emails.${i18nKey}.title`}
        />
      </Box>
      <Box relative ml4 top={-10}>
        <TooltipIcon
          size={16}
          theme="navy"
          preferPlace="above"
          color="grayMediumDark"
          content={
            <Box w={306}>
              <Text
                fs14
                fw600
                white
                i18n={`message_drawer.team_emails.${i18nKey}.desc`}
              />
            </Box>
          }
        />
      </Box>
    </Box>
  );

  renderTeamAnnouncementsCheckbox = () => {
    if (this.props.showAnnouncementsV2) {
      const { pinned, requireAcknowledgement } = this.state;
      return (
        <Box ml={-7} mt24>
          <Box>
            <CheckboxField
              name="pinned"
              value={pinned}
              onChange={this.handleTogglePinned}
              title={this.renderLabelWithTooltip(
                'pinned_announcement_checkbox'
              )}
            />
          </Box>
          <Box mt4>
            <CheckboxField
              name="require_acknowledgement"
              value={requireAcknowledgement}
              onChange={this.handleToggleRequireAcknowledgement}
              title={this.renderLabelWithTooltip(
                'require_acknowledgement_checkbox'
              )}
            />
          </Box>
        </Box>
      );
    }

    const { pinned } = this.state;

    // hide the description when the checkbox is disabled
    const desc = pinned ? (
      <Text
        fs12
        grayMediumDark
        i18n="message_drawer.team_emails.announcements_checkbox.desc"
      />
    ) : null;

    return (
      <Box ml={-7} mt24>
        <CheckboxField
          name="pinned"
          value={pinned}
          onChange={this.handleTogglePinned}
          title={
            <Text
              navy
              i18n="message_drawer.team_emails.announcements_checkbox.title"
            />
          }
          desc={desc}
        />
      </Box>
    );
  };

  renderBody() {
    const { subject, message } = this.state;
    const { showAnnouncementsV2 } = this.props;

    return (
      <Box ph32>
        {this.locationOptions.length > 1 && this.renderLocationsSelect()}

        {this.renderLabel('message_drawer.team_emails.audience_label')}
        <SelectInput
          onChange={this.handleAudienceChange}
          options={this.audienceOptions}
          value={this.state.audience}
          clearable={false}
        />

        <Box mt24>
          <TextInput
            name="subject"
            onChange={this.handleSubjectChange}
            value={subject}
            placeholder={toI18n(
              'message_drawer.team_emails.subject_placeholder'
            )}
            valueOnChange
          />
        </Box>

        <Box mt12>
          <Box b={showAnnouncementsV2} bb={false}>
            <TextInput
              name="message"
              type="textarea"
              size="tall"
              onChange={this.handleMessageChange}
              value={message}
              border={!showAnnouncementsV2}
              placeholder={toI18n(
                'message_drawer.team_emails.message_placeholder'
              )}
              valueOnChange
            />
          </Box>

          {showAnnouncementsV2 && this.state.files.length === 0 && (
            <Box b ph={12} onClick={this.handleShowAttachmentScreen} pointer>
              <a>
                <FontAwesomeIcon icon={faPaperclip} />
                <Text
                  lh="38px"
                  i18n="message_drawer.team_emails.add_attachments"
                  fs14
                />
              </a>
            </Box>
          )}

          {showAnnouncementsV2 && this.state.files.length > 0 && (
            <Box
              b
              ph={16}
              pv={8}
              row
              spacebetween
              mb16
              onClick={this.handleShowAttachmentScreen}
              pointer
            >
              <Box row>
                <Box mr={8}>
                  <FontAwesomeIcon icon={faFile} color={colors.blue} />
                </Box>

                <Text blue fw600 fs14>
                  {this.state.files[0].name}
                </Text>
              </Box>

              <Icon type="edit" color="blue" />
            </Box>
          )}
        </Box>

        {this.renderTeamAnnouncementsCheckbox()}

        <Box
          row
          hcenter
          phxl
          pvs
          absolute
          bottom={0}
          left={0}
          w="100%"
          borderbox
        >
          <Button
            theme="primary"
            fullWidth
            onClick={this.handleSend}
            i18n="message_drawer.team_emails.send_message"
            loading={this.state.isSubmitting}
          />
        </Box>
      </Box>
    );
  }

  renderForm() {
    return (
      <div>
        {this.renderHeader()}
        {this.renderBody()}
      </div>
    );
  }

  renderAttachments() {
    return (
      <div>
        <Box pvxs bb row spacebetween vcenter>
          <Box plxxs row vcenter>
            <Box plxxs w={270} ellipsis>
              <Text
                black
                fs18
                fw600
                i18n="message_drawer.team_emails.attachment_title"
              />
            </Box>
          </Box>
          <Box mla>
            <IconButton
              onClick={this.props.onClose}
              type="mdClose"
              iconColor="grayDark"
              size="sm"
            />
          </Box>
        </Box>

        <Box p={32}>
          {this.state.newFiles.map((file, i) => (
            <Box key={i} b ph={16} pv={8} row spacebetween mb16>
              <Box row>
                <Box mr={8}>
                  <FontAwesomeIcon icon={faFile} color={colors.blue} />
                </Box>

                <Text blue fw600 fs14>
                  {file.name}
                </Text>
              </Box>

              <Icon
                type="close"
                color="grayMediumDark"
                onClick={partial(this.handleRemoveFile, file)}
              />
            </Box>
          ))}
          <Dropzone
            onChange={this.handleDropFile}
            theme="blue"
            acceptedFileTypes={FILE_TYPES}
            supportedi18n="message_drawer.team_emails.file_types"
            displayFile={false}
          />

          <Box
            row
            hcenter
            phxl
            pvs
            absolute
            bottom={0}
            left={0}
            w="100%"
            borderbox
          >
            <Box mr={12} grow>
              <Button
                theme="default"
                fullWidth
                onClick={this.handleCancel}
                i18n="actions.cancel"
              />
            </Box>

            <Box grow>
              <Button
                theme="primary"
                fullWidth
                onClick={this.handleUpload}
                i18n="actions.upload"
              />
            </Box>
          </Box>
        </Box>
      </div>
    );
  }

  render() {
    return (
      <Box className={this.cx()} relative h="100%">
        {this.state.view === 'form'
          ? this.renderForm()
          : this.renderAttachments()}
      </Box>
    );
  }
}
